import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import remaxAlfaLogo from "@/assets/remax-alfa-logo-white-v2.png";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CONSENT_CHANGED_EVENT, hasMarketingConsent } from "@/lib/consent";

const ease = [0.22, 1, 0.36, 1] as const;

const navLinks = [
  { label: "Domů", href: "/" },
  { label: "O mně", href: "/o-mne" },
  { label: "Aktuální nabídka", href: "/#nabidka" },
  { label: "Služby", href: "/#sluzby" },
  { label: "Reference", href: "/reference" },
  { label: "Články", href: "/clanky" },
  { label: "Kontakt", href: "/kontakt" },
];

const FooterSection = () => {
  const [fbAllowed, setFbAllowed] = useState(hasMarketingConsent);
  useEffect(() => {
    const on = () => setFbAllowed(hasMarketingConsent());
    window.addEventListener(CONSENT_CHANGED_EVENT, on);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, on);
  }, []);
  return (
    <footer id="kontakt" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(220,15%,12%)] to-[hsl(0,0%,3%)]" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="container-luxury section-padding pb-10 relative z-10">
        {/* Top: Brand row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-14 pb-10 border-b border-primary-foreground/[0.08]"
        >
          <div className="flex items-center gap-5">
            <img src={remaxAlfaLogo} alt="RE/MAX Alfa" className="h-14" />
            <div>
              <h3 className="font-heading font-bold text-xl tracking-tight text-white">
                František Babčický
              </h3>
              <p className="text-primary-foreground/40 text-sm mt-0.5 max-w-xs">
                Váš realitní partner s 30 lety zkušeností. Člen RE/MAX Hall of Fame a držitel ocenění Lifetime Achievement.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <motion.a
              href="https://www.instagram.com/frantisek.babcicky/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, y: -3 }}
              whileTap={{ scale: 0.95 }}
              className="w-11 h-11 rounded-xl bg-primary-foreground/[0.06] border border-primary-foreground/[0.08] flex items-center justify-center hover:bg-primary/20 hover:border-primary/30 transition-all duration-300"
              aria-label="Instagram"
            >
              <Instagram className="h-[18px] w-[18px] text-primary-foreground/60" />
            </motion.a>
            <motion.a
href="https://www.facebook.com/frantisek.babcicky"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, y: -3 }}
              whileTap={{ scale: 0.95 }}
              className="w-11 h-11 rounded-xl bg-primary-foreground/[0.06] border border-primary-foreground/[0.08] flex items-center justify-center hover:bg-primary/20 hover:border-primary/30 transition-all duration-300"
              aria-label="Facebook"
            >
              <Facebook className="h-[18px] w-[18px] text-primary-foreground/60" />
            </motion.a>
          </div>
        </motion.div>

        {/* Bottom: 3 columns */}
        <div className="grid md:grid-cols-3 gap-12 lg:gap-16 mb-16">
          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease }}
          >
            <h4 className="font-heading font-semibold text-lg mb-6 tracking-tight text-white">Kontakt</h4>
            <div className="space-y-4">
              <motion.a
                whileHover={{ x: 4 }}
                href="tel:+420733679995"
                className="group flex items-center gap-3 text-primary-foreground/55 hover:text-primary-foreground transition-all duration-300 text-sm"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-foreground/[0.06] border border-primary-foreground/[0.08] flex items-center justify-center group-hover:bg-primary/20 group-hover:border-primary/30 transition-all duration-300">
                  <Phone className="h-3.5 w-3.5" />
                </div>
                <span className="font-medium">+420 733 679 995</span>
              </motion.a>
              <motion.a
                whileHover={{ x: 4 }}
                href="mailto:frantisek.babcicky@re-max.cz"
                className="group flex items-center gap-3 text-primary-foreground/55 hover:text-primary-foreground transition-all duration-300 text-sm"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-foreground/[0.06] border border-primary-foreground/[0.08] flex items-center justify-center group-hover:bg-primary/20 group-hover:border-primary/30 transition-all duration-300">
                  <Mail className="h-3.5 w-3.5" />
                </div>
                <span className="font-medium">frantisek.babcicky@re-max.cz</span>
              </motion.a>
              <div className="group flex items-start gap-3 text-primary-foreground/55 text-sm">
                <div className="w-9 h-9 rounded-lg bg-primary-foreground/[0.06] border border-primary-foreground/[0.08] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="h-3.5 w-3.5" />
                </div>
                <div>
                  <span className="font-medium block">Spálená 29/97, Praha 1 – Nové Město</span>
                  <span className="font-medium block mt-1">Ke Kříži 1653, Praha 10 – Uhříněves</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2, ease }}
          >
            <h4 className="font-heading font-semibold text-lg mb-6 tracking-tight text-white">Navigace</h4>
            <div className="space-y-1">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.05, ease }}
                >
                  <Link
                    to={link.href}
                    onClick={() => {
                      if (link.href === "/") {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    className="group flex items-center gap-1.5 py-1.5 text-sm text-primary-foreground/55 hover:text-primary-foreground transition-all duration-300"
                  >
                    <span>{link.label}</span>
                    <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all duration-300" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Facebook */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.3, ease }}
          >
            <h4 className="font-heading font-semibold text-lg mb-6 tracking-tight text-white">Facebook</h4>
            {fbAllowed ? (
              <div className="rounded-xl overflow-hidden" style={{ maxWidth: 340 }}>
                <iframe
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Ffrantisek.babcicky&tabs=timeline&width=340&height=300&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                  width="340"
                  height="300"
                  style={{ border: "none", overflow: "hidden" }}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Facebook – František Babčický"
                />
              </div>
            ) : (
              <a
                href="https://www.facebook.com/frantisek.babcicky"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary-foreground/55 hover:text-primary-foreground transition-colors"
              >
                <Facebook className="h-4 w-4" />
                Sledujte mě na Facebooku
              </a>
            )}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="border-t border-primary-foreground/[0.08] pt-8 text-center"
        >
          <p className="text-xs text-primary-foreground/25 tracking-wide">
            © {new Date().getFullYear()} František Babčický · RE/MAX Alfa. Všechna práva vyhrazena.
          </p>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
            className="mt-2 text-xs text-primary-foreground/40 hover:text-primary-foreground underline transition-colors"
          >
            Nastavení cookies
          </button>
        </motion.div>
      </div>
    </footer>
  );
};

export default FooterSection;